import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { searchPlugin } from "@react-pdf-viewer/search";
import { X, Loader2, FileWarning, Download } from "lucide-react";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";
import { documentsApi } from "../../../services/ragApi";
import { extractSearchableChunkText } from "../utils/extractSearchableText";
import "./DocumentViewerModal.css";

// Worker pdfjs servito dal frontend public/, copiato in build a /pdf.worker.min.js
const WORKER_URL = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

export default function DocumentViewerModal({ documentId, filename, chunkContent, isOpen, onClose }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const isPdf = useMemo(() => filename.toLowerCase().endsWith(".pdf"), [filename]);

    const searchKeywords = useMemo(
        () => extractSearchableChunkText(chunkContent || ""),
        [chunkContent],
    );

    // I plugin di @react-pdf-viewer usano hooks internamente: NON wrappare in useMemo
    // (viola Rules of Hooks). Vanno istanziati ad ogni render.
    const searchPluginInstance = searchPlugin({
        keyword: searchKeywords.length > 0 ? searchKeywords : undefined,
    });
    const defaultLayoutPluginInstance = defaultLayoutPlugin();

    // Escape chiude il modal
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [isOpen, onClose]);

    // Fetch del blob PDF (solo quando modal aperto e file e' PDF)
    useEffect(() => {
        if (!isOpen || !isPdf) return;

        let cancelled = false;
        let createdUrl = null;

        setLoading(true);
        setError(null);
        setBlobUrl(null);

        documentsApi
            .fetchFile(documentId)
            .then((blob) => {
                if (cancelled) return;
                createdUrl = URL.createObjectURL(blob);
                setBlobUrl(createdUrl);
            })
            .catch((err) => {
                if (cancelled) return;
                const status = err?.response?.status;
                if (status === 404) setError("Documento non disponibile sul server.");
                else if (status === 403) setError("Non hai i permessi per visualizzare questo documento.");
                else setError("Errore nel caricamento del documento.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [isOpen, isPdf, documentId]);

    // Dopo il load del PDF: highlight + jump alla prima occorrenza, con retry per pagine lente.
    const handleDocumentLoad = async () => {
        if (searchKeywords.length === 0) return;
        try {
            await new Promise((r) => setTimeout(r, 300));
            const matches = await searchPluginInstance.highlight(searchKeywords);
            if (matches && matches.length > 0) {
                searchPluginInstance.jumpToMatch(0);
            } else {
                await new Promise((r) => setTimeout(r, 800));
                const retryMatches = await searchPluginInstance.highlight(searchKeywords);
                if (retryMatches && retryMatches.length > 0) {
                    searchPluginInstance.jumpToMatch(0);
                }
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[PDFViewer] Highlight/jump failed", err);
        }
    };

    const handleDownload = async () => {
        try {
            const blob = await documentsApi.fetchFile(documentId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            setError("Errore durante il download.");
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="rag-viewer-backdrop"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            {/* Nasconde "Show Properties" dal menu del viewer (testid stabili in @react-pdf-viewer/properties) */}
            <style>{`
                [data-testid="properties__button"],
                [data-testid="properties__menu"] {
                    display: none !important;
                }
            `}</style>
            <div className="rag-viewer-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rag-viewer-header">
                    <h3 className="rag-viewer-title" title={filename}>
                        {filename}
                    </h3>
                    <button onClick={onClose} className="rag-viewer-close" aria-label="Chiudi">
                        <X size={18} />
                    </button>
                </div>

                <div className="rag-viewer-body">
                    {!isPdf ? (
                        <div className="rag-viewer-fallback">
                            <FileWarning size={36} />
                            <p>Anteprima disponibile solo per PDF.</p>
                            <button onClick={handleDownload} className="rag-viewer-download">
                                <Download size={14} />
                                <span>Scarica</span>
                            </button>
                        </div>
                    ) : loading ? (
                        <div className="rag-viewer-loading">
                            <Loader2 className="rag-viewer-spinner" size={20} />
                            <span>Caricamento PDF…</span>
                        </div>
                    ) : error ? (
                        <div className="rag-viewer-fallback">
                            <FileWarning size={36} className="rag-viewer-fallback-icon" />
                            <p>{error}</p>
                        </div>
                    ) : blobUrl ? (
                        <Worker workerUrl={WORKER_URL}>
                            <div className="rag-viewer-pdf">
                                <Viewer
                                    fileUrl={blobUrl}
                                    plugins={[defaultLayoutPluginInstance, searchPluginInstance]}
                                    onDocumentLoad={handleDocumentLoad}
                                />
                            </div>
                        </Worker>
                    ) : null}
                </div>
            </div>
        </div>,
        document.body,
    );
}
